import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { X, ArrowUpRight, ArrowRight, Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { IMAGES, SHOWREEL_VIDEO_URL, BRAND_LOGO_URL, PARTNERS } from "@/data/content";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { NewsPreview } from "@/pages/News";

const EASE = [0.16, 1, 0.3, 1];

const HeroLine = ({ text, serif, delay }) => (
  <div className="overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
      className={`block f-display font-black tracking-tight leading-[0.95] text-[13vw] sm:text-7xl lg:text-8xl ${
        serif ? "f-serif italic font-normal text-red-500" : "text-slate-50"
      }`}
    >
      {text}
    </motion.span>
  </div>
);

const ApertureRings = ({ x, y }) => (
  <motion.div
    className="absolute -right-32 top-1/2 -translate-y-1/2 w-[560px] h-[560px] pointer-events-none hidden lg:block"
    style={{ x, y }}
    aria-hidden="true"
  >
    <div className="absolute inset-0 spotlight-red" />
    <svg viewBox="0 0 400 400" className="w-full h-full spin-slow opacity-40">
      <circle cx="200" cy="200" r="180" fill="none" stroke="rgba(229,9,20,0.5)" strokeWidth="1" strokeDasharray="4 10" />
      <circle cx="200" cy="200" r="130" fill="none" stroke="rgba(248,250,252,0.18)" strokeWidth="1" />
    </svg>
    <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full spin-slower opacity-30">
      <circle cx="200" cy="200" r="90" fill="none" stroke="rgba(245,158,11,0.45)" strokeWidth="1" strokeDasharray="2 8" />
    </svg>
  </motion.div>
);

const Hero = () => {
  const { t, lang } = useLanguage();
  const [reel, setReel] = useState(false);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const ringsX = useTransform(mx, (v) => v * -28);
  const ringsY = useTransform(my, (v) => v * -18);
  const imgX = useTransform(mx, (v) => v * 12);

  return (
    <section
      className="relative overflow-hidden"
      data-testid="hero-section"
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
      }}
    >
      <div className="absolute inset-0" aria-hidden="true">
        <motion.img
          src={IMAGES.hero}
          alt=""
          className="w-full h-full object-cover opacity-25 scale-110"
          style={{ x: imgX }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08080A]/70 via-[#08080A]/85 to-[#08080A]" />
      </div>
      <ApertureRings x={ringsX} y={ringsY} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 md:pt-32 pb-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="f-mono text-[11px] uppercase tracking-[0.3em] text-red-500 mb-8"
          data-testid="hero-kicker"
        >
          {t.hero.kicker}
        </motion.p>

        <h1 className="max-w-5xl" key={lang} data-testid="hero-headline">
          {t.hero.lines.map((l, i) => (
            <HeroLine key={i} text={l.text} serif={l.serif} delay={0.2 + i * 0.14} />
          ))}
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.9, ease: EASE }}
            className="text-slate-400 text-base sm:text-lg leading-relaxed max-w-xl"
            data-testid="hero-subheadline"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
            className="flex items-center gap-4"
          >
            <button
              onClick={() => setReel(true)}
              data-testid="hero-showreel-play-button"
              className="pulse-ring relative w-20 h-20 rounded-full bg-white p-2.5 flex items-center justify-center shrink-0 overflow-hidden transition-transform duration-300 hover:scale-110"
              aria-label={t.hero.ctaShowreel}
            >
              <img src={BRAND_LOGO_URL} alt="RV FILM — Radiovilles" className="w-full h-full object-contain" />
            </button>
            <span className="f-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 max-w-[120px]">
              {t.hero.ctaShowreel}
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 1, ease: EASE }}
          className="mt-16 letterbox relative overflow-hidden rounded-lg border border-white/10 group cursor-pointer"
          onClick={() => setReel(true)}
          data-testid="showreel-frame"
        >
          <img
            src={IMAGES.showreel}
            alt="RV FILM showreel"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#08080A]/80 via-transparent to-transparent" />
          <div className="absolute bottom-4 left-5 f-mono text-[10px] tracking-[0.25em] text-slate-300 uppercase">
            Showreel · 2026
          </div>
          <div className="absolute bottom-4 right-5 f-mono text-[10px] tracking-[0.25em] text-red-500 uppercase">
            REC ●
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-8 f-mono text-[10px] tracking-[0.25em] uppercase text-slate-600"
          data-testid="hero-location"
        >
          {t.hero.location}
        </motion.p>
      </div>

      <AnimatePresence>
        {reel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-12"
            onClick={() => setReel(false)}
            data-testid="showreel-modal"
          >
            <button
              className="absolute top-6 right-6 text-slate-300 hover:text-white p-2"
              onClick={() => setReel(false)}
              data-testid="showreel-close"
              aria-label="Close"
            >
              <X size={28} />
            </button>
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="w-full max-w-5xl aspect-video rounded-lg overflow-hidden border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <video
                src={SHOWREEL_VIDEO_URL}
                autoPlay
                controls
                playsInline
                className="w-full h-full bg-black"
                data-testid="showreel-video"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const Stats = () => {
  const { t } = useLanguage();
  return (
    <section className="py-24 md:py-36 px-6 lg:px-12" data-testid="stats-section" id="risultati">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-red-500 mb-6">{t.stats.kicker}</p>
          <h2 className="f-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight max-w-3xl">
            {t.stats.titleA}
            <span className="f-serif italic font-normal text-red-500 block mt-1">{t.stats.titleB}</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-lg overflow-hidden">
          {t.stats.items.map((s, i) => (
            <Reveal key={i} delay={i * 0.1} className="bg-[#101114] p-8 md:p-10">
              <p className="f-display text-4xl md:text-6xl font-black tracking-tight text-slate-50" data-testid={`stat-value-${i}`}>
                {s.value}
              </p>
              <p className="mt-4 text-sm text-slate-400 leading-snug">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const { t } = useLanguage();
  const cards = [
    {
      slug: "adv",
      img: IMAGES.tabletopPerfume,
      video: "https://customer-assets-cm19k8pv.emergentagent.net/job_rvfilm-digital/artifacts/f1c261e858ce798f_remote%20streaming.mp4",
      span: "md:col-span-7",
      data: t.services.adv,
    },
    { slug: "digital", img: IMAGES.digital[0], video: "https://customer-assets-cm19k8pv.emergentagent.net/job_rvfilm-digital/artifacts/0ea18b7aa7c29d2f_Hornimans%20Tea%20-%20dir.%20Eduardo%20Vieitez%281%29.mp4", span: "md:col-span-5", data: t.services.digital },
    { slug: "corporate", img: IMAGES.corporate[0], span: "md:col-span-5", data: t.services.corporate },
    { slug: "ai", img: IMAGES.ai[0], video: "https://customer-assets-cm19k8pv.emergentagent.net/job_rvfilm-digital/artifacts/4148e78522f4e56e_Mountain%20Coffee%20AI%281%29.mp4", span: "md:col-span-7", data: t.services.ai },
  ];
  return (
    <section className="py-24 md:py-36 px-6 lg:px-12 bg-[#0B0B0E]" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-red-500 mb-6">{t.services.kicker}</p>
          <h2 className="f-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl">
            {t.services.title}
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
          {cards.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.08} className={`col-span-1 ${c.span}`}>
              <Link
                to={`/${c.slug}`}
                data-testid={`service-card-${c.slug}`}
                className="group relative block h-[380px] md:h-[440px] rounded-lg overflow-hidden border border-white/10 hover:border-red-600/40 transition-colors duration-500"
              >
                {c.video ? (
                  <video
                    src={c.video}
                    autoPlay
                    muted
                    loop
                    playsInline
                    aria-label={c.data.name}
                    className="absolute inset-0 w-full h-full object-cover opacity-60 transition-all duration-700 group-hover:scale-105 group-hover:opacity-80"
                  />
                ) : (
                  <img
                    src={c.img}
                    alt={c.data.name}
                    className="absolute inset-0 w-full h-full object-cover opacity-60 transition-all duration-700 group-hover:scale-105 group-hover:opacity-80"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#08080A] via-[#08080A]/40 to-transparent" />
                <div className="absolute inset-0 p-8 flex flex-col justify-end">
                  <p className="f-mono text-[10px] uppercase tracking-[0.25em] text-red-500 mb-3">{c.data.tagline}</p>
                  <h3 className="f-display text-3xl md:text-4xl font-black tracking-tight">{c.data.name}</h3>
                  <p className="mt-3 text-sm text-slate-400 max-w-md leading-relaxed">{c.data.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-2 f-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 group-hover:text-red-500 transition-colors">
                    {t.services.discover}
                    <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Process = () => {
  const { t } = useLanguage();
  return (
    <section className="py-24 md:py-36 px-6 lg:px-12" data-testid="process-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-red-500 mb-6">{t.process.kicker}</p>
            <h2 className="f-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              {t.process.title}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 letterbox rounded-lg overflow-hidden border border-white/10">
              <img src={IMAGES.editing[0]} alt="Post-production suite" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7 flex flex-col justify-center">
          {t.process.steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="flex gap-6 md:gap-10 py-8 border-t border-white/10 group" data-testid={`process-step-${s.n}`}>
                <span className="f-mono text-sm text-red-500 pt-1">{s.n}</span>
                <div>
                  <h3 className="f-display text-2xl md:text-3xl font-bold tracking-tight group-hover:text-red-500 transition-colors duration-300">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm md:text-base text-slate-400 leading-relaxed max-w-xl">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhyUs = () => {
  const { t } = useLanguage();
  return (
    <section className="py-24 md:py-36 px-6 lg:px-12 bg-[#0B0B0E]" data-testid="why-us-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="rounded-lg overflow-hidden border border-white/10 aspect-[4/3]">
            <img src={IMAGES.studioLighting} alt="Studio lighting" className="w-full h-full object-cover" />
          </div>
        </Reveal>
        <div>
          <Reveal>
            <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-red-500 mb-6">{t.why.kicker}</p>
            <h2 className="f-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              {t.why.title}
            </h2>
            <p className="mt-6 text-slate-400 leading-relaxed">{t.why.desc}</p>
          </Reveal>
          <ul className="mt-8 space-y-4">
            {t.why.points.map((p, i) => (
              <Reveal key={i} delay={0.1 + i * 0.08}>
                <li className="flex items-start gap-3 text-slate-300 text-sm md:text-base" data-testid={`why-point-${i}`}>
                  <Check size={18} className="text-red-500 mt-0.5 shrink-0" />
                  {p}
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

const Partners = () => {
  const { t } = useLanguage();
  return (
    <section className="py-20 px-6 lg:px-12 border-t border-white/10" data-testid="partners-section">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <p className="f-mono text-[11px] uppercase tracking-[0.3em] text-red-500 mb-3">{t.partners.kicker}</p>
          <h2 className="f-display text-2xl md:text-3xl font-bold tracking-tight mb-12">{t.partners.title}</h2>
        </Reveal>
        <div className="flex flex-wrap items-center gap-x-14 gap-y-8">
          {PARTNERS.map((p, i) => (
            <Reveal key={p} delay={i * 0.06}>
              <span className="f-display text-xl md:text-2xl font-extrabold tracking-tight text-slate-600 hover:text-slate-200 transition-colors duration-500 cursor-default" data-testid={`partner-${i}`}>
                {p}
              </span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const CtaBand = () => {
  const { t } = useLanguage();
  return (
    <section className="relative py-28 md:py-40 px-6 lg:px-12 overflow-hidden" data-testid="cta-band">
      <img src={IMAGES.ctaBg} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#08080A] via-[#08080A]/80 to-[#08080A]/40" />
      <div className="absolute -left-40 top-1/2 -translate-y-1/2 w-[500px] h-[500px] spotlight-red" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto">
        <Reveal>
          <h2 className="f-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight leading-[0.95]">
            {t.cta.titleA}
            <span className="f-serif italic font-normal text-red-500 block">{t.cta.titleB}</span>
          </h2>
          <p className="mt-6 text-slate-400 max-w-xl leading-relaxed">{t.cta.desc}</p>
          <Link
            to="/contatti"
            data-testid="cta-band-button"
            className="group mt-10 inline-flex items-center gap-3 bg-red-600 hover:bg-red-500 transition-colors duration-300 text-white f-mono text-xs uppercase tracking-[0.2em] px-8 py-4 rounded-full"
          >
            {t.cta.button}
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useLanguage();
  return (
    <main data-testid="home-page">
      <Seo title={t.seo.homeTitle} description={t.seo.homeDesc} />
      <Hero />
      <Marquee />
      <Services />
      <Stats />
      <Process />
      <WhyUs />
      <NewsPreview />
      <Partners />
      <CtaBand />
    </main>
  );
}
